import { useEffect, useState } from 'react'
import { Link, usePage } from '@inertiajs/react'
import { PageProps } from '@/types'
import SlideOver from './SlideOver'
import { ExternalLink, Save, X, Edit, Loader2, AlertCircle, Check } from 'lucide-react'

interface CommissionData {
  id: number
  agent_id: number
  agent_name: string
  carrier_id: number
  carrier_name: string
  policy_number: string
  commission_type: string
  gross_amount: number
  agent_amount: number
  override_amount: number | null
  agent_split_percentage: number | null
  sponsor_split_percentage: number | null
  sponsor_amount: number | null
  supplement_amount: number
  carrier_payout_percentage: number | null
  parent_agent_id: number | null
  parent_agent_name: string | null
  period_start: string
  period_end: string
  status: string
  created_at: string
}

interface SelectOption { id: number; name: string }

interface Props {
  commissionId: number | null
  isOpen: boolean
  onClose: () => void
  onSaved?: () => void
}

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700'
}

const formatCurrency = (amount: number) =>
  `$${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export default function CommissionDetailPanel({ commissionId, isOpen, onClose, onSaved }: Props) {
  const { auth, ...pageProps } = usePage<PageProps>().props
  const isAdmin = auth.user?.role === 'owner' || auth.user?.role === 'administrator'
  const csrfFromProps = (pageProps as { csrf_token?: string }).csrf_token

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [commission, setCommission] = useState<CommissionData | null>(null)
  const [agents, setAgents] = useState<SelectOption[]>([])
  const [carriers, setCarriers] = useState<SelectOption[]>([])
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<{
    agent_id: number
    carrier_id: number
    policy_number: string
    commission_type: string
    gross_amount: number
    agent_amount: number
    sponsor_amount: number
    agent_split_percentage: number
    sponsor_split_percentage: number
    period_start: string
    period_end: string
    status: string
  } | null>(null)

  useEffect(() => {
    if (!isOpen || !commissionId) return
    setLoading(true)
    setError(null)
    setIsEditing(false)
    setCommission(null)

    fetch(`/commissions/${commissionId}`, {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load commission (${res.status})`)
        return res.json()
      })
      .then((data) => {
        setCommission(data.commission)
        setAgents(data.agents || [])
        setCarriers(data.carriers || [])
        setForm({
          agent_id: data.commission.agent_id,
          carrier_id: data.commission.carrier_id,
          policy_number: data.commission.policy_number,
          commission_type: data.commission.commission_type,
          gross_amount: data.commission.gross_amount,
          agent_amount: data.commission.agent_amount,
          sponsor_amount: data.commission.sponsor_amount || 0,
          agent_split_percentage: data.commission.agent_split_percentage || 0,
          sponsor_split_percentage: data.commission.sponsor_split_percentage || 0,
          period_start: data.commission.period_start || '',
          period_end: data.commission.period_end || '',
          status: data.commission.status,
        })
      })
      .catch((err) => setError(err.message || 'Failed to load commission'))
      .finally(() => setLoading(false))
  }, [commissionId, isOpen])

  const updateAgentAmount = (newAgentAmount: number) => {
    if (!form) return
    const gross = form.gross_amount
    const sponsorAmt = Math.max(0, gross - newAgentAmount)
    setForm({
      ...form,
      agent_amount: newAgentAmount,
      sponsor_amount: sponsorAmt,
      agent_split_percentage: gross > 0 ? Math.round((newAgentAmount / gross) * 10000) / 100 : 0,
      sponsor_split_percentage: gross > 0 ? Math.round((sponsorAmt / gross) * 10000) / 100 : 0,
    })
  }

  const updateSponsorAmount = (newSponsorAmount: number) => {
    if (!form) return
    const gross = form.gross_amount
    const agentAmt = Math.max(0, gross - newSponsorAmount)
    setForm({
      ...form,
      agent_amount: agentAmt,
      sponsor_amount: newSponsorAmount,
      agent_split_percentage: gross > 0 ? Math.round((agentAmt / gross) * 10000) / 100 : 0,
      sponsor_split_percentage: gross > 0 ? Math.round((newSponsorAmount / gross) * 10000) / 100 : 0,
    })
  }

  const csrfToken = () =>
    csrfFromProps ||
    (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ||
    ''

  const handleSave = async () => {
    if (!commission || !form) return
    setSaving(true)
    setError(null)
    try {
      const res = await fetch(`/commissions/${commission.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ commission: form }),
      })
      if (res.status === 422) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body.errors || []).join(', ') || 'Save failed')
      }
      if (!res.ok) {
        throw new Error(`Save failed (${res.status})`)
      }
      const refreshed = await fetch(`/commissions/${commission.id}`, {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json())
      setCommission(refreshed.commission)
      setIsEditing(false)
      if (onSaved) onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const handleApprove = async () => {
    if (!commission) return
    setSaving(true)
    setError(null)
    try {
      const res = await fetch(`/commissions/${commission.id}/approve`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      })
      if (!res.ok && res.status !== 302) {
        throw new Error(`Approve failed (${res.status})`)
      }
      const refreshed = await fetch(`/commissions/${commission.id}`, {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json())
      setCommission(refreshed.commission)
      if (onSaved) onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approve failed')
    } finally {
      setSaving(false)
    }
  }

  const title = commission
    ? `Commission #${commission.policy_number}`
    : 'Commission Details'

  const hasSplit = commission?.agent_split_percentage != null && commission.agent_split_percentage > 0

  return (
    <SlideOver isOpen={isOpen} onClose={onClose} title={title}>
      {loading && (
        <div className="flex items-center justify-center py-12 text-chestnut-charcoal/60">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {error && !loading && (
        <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start gap-2">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <div className="text-sm">{error}</div>
        </div>
      )}

      {commission && form && !loading && (
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${statusColors[commission.status]}`}>
              {commission.status}
            </span>
            <div className="flex items-center gap-2">
              {isAdmin && commission.status === 'pending' && !isEditing && (
                <button onClick={handleApprove} className="btn-primary flex items-center gap-2 text-xs">
                  <Check className="h-4 w-4" />
                  Approve
                </button>
              )}
              {isAdmin && !isEditing && (
                <button onClick={() => setIsEditing(true)} className="btn-secondary flex items-center gap-2 text-xs">
                  <Edit className="h-4 w-4" />
                  Edit
                </button>
              )}
              {isAdmin && isEditing && (
                <>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="btn-secondary flex items-center gap-2 text-xs"
                    disabled={saving}
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="btn-primary flex items-center gap-2 text-xs"
                    disabled={saving}
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="card p-4 space-y-4">
            <h3 className="font-semibold text-chestnut-charcoal text-sm border-b border-chestnut-charcoal/10 pb-2">
              Commission Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Agent">
                {isEditing ? (
                  <select
                    value={form.agent_id}
                    onChange={(e) => setForm({ ...form, agent_id: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-chestnut-charcoal/20 rounded-lg text-sm"
                  >
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                ) : (
                  <Link href={`/agents/${commission.agent_id}`} className="text-chestnut-forest hover:underline font-medium">
                    {commission.agent_name}
                  </Link>
                )}
              </Field>

              <Field label="Carrier">
                {isEditing ? (
                  <select
                    value={form.carrier_id}
                    onChange={(e) => setForm({ ...form, carrier_id: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-chestnut-charcoal/20 rounded-lg text-sm"
                  >
                    {carriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                ) : (
                  <span className="text-chestnut-charcoal">{commission.carrier_name}</span>
                )}
              </Field>

              <Field label="Policy Number">
                {isEditing ? (
                  <input
                    type="text"
                    value={form.policy_number}
                    onChange={(e) => setForm({ ...form, policy_number: e.target.value })}
                    className="w-full px-3 py-2 border border-chestnut-charcoal/20 rounded-lg text-sm"
                  />
                ) : (
                  <span className="font-mono text-sm">{commission.policy_number}</span>
                )}
              </Field>

              <Field label="Type">
                {isEditing ? (
                  <select
                    value={form.commission_type}
                    onChange={(e) => setForm({ ...form, commission_type: e.target.value })}
                    className="w-full px-3 py-2 border border-chestnut-charcoal/20 rounded-lg text-sm"
                  >
                    <option value="first_year">First Year</option>
                    <option value="renewal">Renewal</option>
                    <option value="override">Override</option>
                  </select>
                ) : (
                  <span className="capitalize">{commission.commission_type.replace(/_/g, ' ')}</span>
                )}
              </Field>

              <Field label="Period Start">
                {isEditing ? (
                  <input
                    type="date"
                    value={form.period_start}
                    onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                    className="w-full px-3 py-2 border border-chestnut-charcoal/20 rounded-lg text-sm"
                  />
                ) : (
                  <span>{commission.period_start ? new Date(commission.period_start).toLocaleDateString() : '—'}</span>
                )}
              </Field>

              <Field label="Period End">
                {isEditing ? (
                  <input
                    type="date"
                    value={form.period_end}
                    onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                    className="w-full px-3 py-2 border border-chestnut-charcoal/20 rounded-lg text-sm"
                  />
                ) : (
                  <span>{commission.period_end ? new Date(commission.period_end).toLocaleDateString() : '—'}</span>
                )}
              </Field>

              {isEditing && (
                <Field label="Status">
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full px-3 py-2 border border-chestnut-charcoal/20 rounded-lg text-sm"
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="paid">Paid</option>
                  </select>
                </Field>
              )}
            </div>
          </div>

          <div className="card p-4 space-y-4">
            <h3 className="font-semibold text-chestnut-charcoal text-sm border-b border-chestnut-charcoal/10 pb-2">
              Amounts
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Gross Amount">
                {isEditing ? (
                  <input
                    type="number"
                    step="0.01"
                    value={form.gross_amount}
                    onChange={(e) => {
                      const newGross = parseFloat(e.target.value) || 0
                      const agentPct = form.agent_split_percentage || 100
                      const agentAmt = Math.round(newGross * agentPct) / 100
                      setForm({
                        ...form,
                        gross_amount: newGross,
                        agent_amount: agentAmt,
                        sponsor_amount: newGross - agentAmt,
                      })
                    }}
                    className="w-full px-3 py-2 border border-chestnut-charcoal/20 rounded-lg text-sm"
                  />
                ) : (
                  <span className="text-lg font-bold text-chestnut-charcoal">{formatCurrency(commission.gross_amount)}</span>
                )}
              </Field>

              <Field label={`Agent Amount${hasSplit ? ` (${commission.agent_split_percentage}%)` : ''}`}>
                {isEditing ? (
                  <input
                    type="number"
                    step="0.01"
                    value={form.agent_amount}
                    onChange={(e) => updateAgentAmount(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-chestnut-charcoal/20 rounded-lg text-sm"
                  />
                ) : (
                  <span className="text-lg font-semibold text-chestnut-forest">{formatCurrency(commission.agent_amount)}</span>
                )}
              </Field>

              {(isEditing || (commission.sponsor_amount || 0) > 0) && (
                <Field label={`Sponsor Amount${hasSplit && !isEditing ? ` (${commission.sponsor_split_percentage}%)` : ''}`}>
                  {isEditing ? (
                    <input
                      type="number"
                      step="0.01"
                      value={form.sponsor_amount}
                      onChange={(e) => updateSponsorAmount(parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-chestnut-charcoal/20 rounded-lg text-sm"
                    />
                  ) : (
                    <>
                      <span className="text-lg font-medium text-chestnut-gold">{formatCurrency(commission.sponsor_amount || 0)}</span>
                      {commission.parent_agent_name && (
                        <div className="text-xs text-chestnut-charcoal/60 mt-1">
                          To:{' '}
                          <Link href={`/agents/${commission.parent_agent_id}`} className="text-chestnut-forest hover:underline">
                            {commission.parent_agent_name}
                          </Link>
                        </div>
                      )}
                    </>
                  )}
                </Field>
              )}
            </div>

            {isEditing && Math.abs((form.agent_amount + form.sponsor_amount) - form.gross_amount) > 0.01 && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
                Agent + Sponsor amounts ({formatCurrency(form.agent_amount + form.sponsor_amount)}) must equal gross ({formatCurrency(form.gross_amount)}).
              </div>
            )}

            {commission.supplement_amount > 0 && !isEditing && (
              <div className="rounded-lg border border-chestnut-gold/40 bg-chestnut-gold/10 p-3 space-y-2">
                <h4 className="font-semibold text-chestnut-charcoal text-sm">Chestnut Carrier Supplement</h4>
                <p className="text-xs text-chestnut-charcoal/70">
                  The carrier paid less than the agent's contracted rate, so Chestnut covered the gap.
                </p>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <div className="text-chestnut-charcoal/60">Carrier Rate</div>
                    <div className="font-semibold">
                      {commission.carrier_payout_percentage != null
                        ? `${Number(commission.carrier_payout_percentage).toFixed(2)}%`
                        : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-chestnut-charcoal/60">Agent Rate</div>
                    <div className="font-semibold">
                      {commission.agent_split_percentage != null
                        ? `${Number(commission.agent_split_percentage).toFixed(2)}%`
                        : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-chestnut-charcoal/60">Supplement</div>
                    <div className="font-bold text-chestnut-gold">{formatCurrency(commission.supplement_amount)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="pt-2">
            <Link
              href={`/commissions/${commission.id}`}
              className="inline-flex items-center gap-2 text-sm text-chestnut-forest hover:underline font-medium"
            >
              <ExternalLink className="h-4 w-4" />
              Open Full Page
            </Link>
          </div>
        </div>
      )}
    </SlideOver>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <label className="block text-xs font-medium text-chestnut-charcoal/60 uppercase tracking-wide mb-1">{label}</label>
      <div className="text-chestnut-charcoal">{children}</div>
    </div>
  )
}
